// Libraries
import _ from 'lodash';

// Supermove
import {gql} from '@supermove/graphql';
import {LineItem} from '@supermove/models';
import {Currency, Float, withFragment} from '@supermove/utils';

// App
import LineItemCategory from '@shared/modules/Billing/enums/LineItemCategory';

/**
 * If price is an empty string, convert it to 0 before a `toMutation` transformation.
 * This is necessary because of the `toForm` transformation on price.
 *
 * Additionally enforce that discount line items always have a negative price.
 */
const toMutationPrice = ({kind, price}: any) => {
  return kind === LineItem.KINDS.DISCOUNT
    ? Math.abs(Currency.toMutation(price || 0)) * -1
    : Currency.toMutation(price || 0);
};

// When submitting the form, if the user input a quantityTwo value
// but no quantity value, then quantityTwo gets saved as quantity
const toMutationQuantity = ({quantity, quantityTwo}: any) => {
  if (!_.isEmpty(quantityTwo) && _.isEmpty(quantity)) {
    return Float.toMutation(quantityTwo);
  }
  if (quantity === '') {
    return null;
  }
  return Float.toMutation(quantity);
};

// When submitting the form, if the user input a higher quantity value
// than the quantityTwo value, then we set quantityTwo to the quantity value
const toMutationQuantityTwo = ({quantityTwo, quantity}: any) => {
  if (
    !_.isEmpty(quantity) &&
    !_.isEmpty(quantityTwo) &&
    Float.toFloat(quantity) > Float.toFloat(quantityTwo)
  ) {
    return Float.toMutation(quantity);
  }
  if (quantityTwo === '') {
    return null;
  }
  return Float.toMutation(quantityTwo);
};

const getCategoryForKind = (kind: any) => {
  switch (kind) {
    case LineItem.KINDS.DISCOUNT:
      return LineItemCategory.EXTRA_DISCOUNT;
    case LineItem.KINDS.FLAT_RATE:
      return LineItemCategory.EXTRA_CHARGE;
    case LineItem.KINDS.HOURLY:
      return LineItemCategory.LABOR;
    case LineItem.KINDS.SUPPLIES:
      return LineItemCategory.SUPPLIES;
    case LineItem.KINDS.ADDITIONAL_HOURS:
      return LineItemCategory.ADDITIONAL_HOURS;
    case LineItem.KINDS.VALUATION_COVERAGE:
      return LineItemCategory.VALUATION_COVERAGE;
    default:
      return LineItemCategory.UNKNOWN;
  }
};

const getUnitForKind = (kind: any) => {
  switch (kind) {
    case LineItem.KINDS.HOURLY:
    case LineItem.KINDS.ADDITIONAL_HOURS:
      return 'hour';
    default:
      return '';
  }
};

const getDisplayPrice = (lineItemForm: any) => {
  // In the case that the user does not enter a price, we default the
  // price to $0.00. This matches what happens in LineItem.getDisplayPrice
  // when there is no price for the lineItem. This also ensures that a value
  // is always provided for the price and we always return a non empty string.
  const price = lineItemForm.price || '$0.00';
  switch (lineItemForm.kind) {
    case LineItem.KINDS.HOURLY:
    case LineItem.KINDS.ADDITIONAL_HOURS:
      return `${price} / hour`;
    default:
      return price;
  }
};

const getQuantity = (lineItemForm: any) => {
  return Float.toFloat(lineItemForm.quantity);
};

const getIsLineItem = (billItemForm: any) => {
  return LineItem.KINDS_ORDER.includes(billItemForm.kind);
};

const getHasQuantityFormula = (lineItemForm: any) => {
  return !!lineItemForm.minQuantityFormulaId || !!lineItemForm.maxQuantityFormulaId;
};

const validate = ({lineItemForm, formField}: any) => {
  const errors = {};
  if (!lineItemForm.kind) {
    _.set(errors, `${formField}.kind`, 'Please select a category');
  }
  if (!lineItemForm.name) {
    _.set(errors, `${formField}.name`, 'Please enter a name');
  }
  return errors;
};

const _new = ({kind}: any) => ({
  nameFormulaId: null,
  priceFormulaId: null,
  minQuantityFormulaId: null,
  maxQuantityFormulaId: null,
  billItemId: null,
  billItemTypeId: null,
  name: '',
  description: '',
  price: '',
  quantity: kind === LineItem.KINDS.FLAT_RATE ? '1' : '',
  quantityTwo: '',
  kind,
  category: '',
  unit: getUnitForKind(kind),
  moverPositionId: null,

  // Private
  isEditable: true,
});

const edit = withFragment(
  (lineItem) => ({
    nameFormulaId: (lineItem as any).nameFormulaId,
    priceFormulaId: (lineItem as any).priceFormulaId,
    minQuantityFormulaId: (lineItem as any).minQuantityFormulaId,
    maxQuantityFormulaId: (lineItem as any).maxQuantityFormulaId,
    billItemId: (lineItem as any).billItemId,
    billItemTypeId: (lineItem as any).billItemTypeId,
    name: (lineItem as any).name,
    description: (lineItem as any).description,
    price: (lineItem as any).price,
    quantity: (lineItem as any).quantity,
    quantityTwo: (lineItem as any).quantityTwo,
    kind: (lineItem as any).kind,
    category: (lineItem as any).category,
    unit: (lineItem as any).unit,
    moverPositionId: (lineItem as any).moverPositionId,
  }),
  gql`
    fragment LineItemForm_edit on LineItem {
      nameFormulaId
      priceFormulaId
      minQuantityFormulaId
      maxQuantityFormulaId
      billItemId
      billItemTypeId
      name
      description
      price
      quantity
      quantityTwo
      kind
      category
      unit
      moverPositionId
    }
  `,
);

const editFromTemplateLineItem = withFragment(
  (templateLineItem) => ({
    nameFormulaId: (templateLineItem as any).nameFormulaId,
    priceFormulaId: (templateLineItem as any).priceFormulaId,
    minQuantityFormulaId: (templateLineItem as any).minQuantityFormulaId,
    maxQuantityFormulaId: (templateLineItem as any).maxQuantityFormulaId,
    billItemId: null,
    billItemTypeId: (templateLineItem as any).billItemTypeId,
    name: (templateLineItem as any).name,
    description: (templateLineItem as any).description,
    price: (templateLineItem as any).price,
    quantity: (templateLineItem as any).kind === LineItem.KINDS.FLAT_RATE ? '1' : '',
    quantityTwo: '',
    kind: (templateLineItem as any).kind,
    category: (templateLineItem as any).category,
    unit: (templateLineItem as any).unit,
  }),
  gql`
    fragment LineItemForm_editFromTemplateLineItem on TemplateLineItem {
      nameFormulaId
      priceFormulaId
      minQuantityFormulaId
      maxQuantityFormulaId
      name
      description
      price
      kind
      category
      unit
      moverPositionId
    }
  `,
);

const toForm = ({
  nameFormulaId,
  priceFormulaId,
  minQuantityFormulaId,
  maxQuantityFormulaId,
  billItemId,
  billItemTypeId,
  name,
  description,
  price,
  quantity,
  quantityTwo,
  kind,
  category,
  unit,
  moverPositionId,
}: any) => ({
  nameFormulaId,
  priceFormulaId,
  minQuantityFormulaId,
  maxQuantityFormulaId,
  billItemId,
  name,
  description: description || '',
  // If price is 0, convert it to an empty string.
  // This prevents forms from starting with $0.00 as an initial value.
  price: price ? Currency.toForm(price) : '',
  quantity: _.isNil(quantity) || (!quantity && !quantityTwo) ? '' : String(quantity),
  quantityTwo: !quantityTwo ? '' : String(quantityTwo),
  kind,
  category,
  unit,
  billItemTypeId,
  moverPositionId,

  // Private
  isEditable: false,
});

const toMutation = ({
  nameFormulaId,
  priceFormulaId,
  minQuantityFormulaId,
  maxQuantityFormulaId,
  billItemId,
  billItemTypeId,
  name,
  description,
  price,
  quantity,
  quantityTwo,
  kind,
  category,
  unit,
  moverPositionId,
}: any) => ({
  nameFormulaId,
  priceFormulaId,
  minQuantityFormulaId,
  maxQuantityFormulaId,
  billItemId,
  billItemTypeId,
  name,
  description,
  price: toMutationPrice({kind, price}),
  quantity: toMutationQuantity({quantity, quantityTwo}),
  quantityTwo: toMutationQuantityTwo({quantityTwo, quantity}),
  kind,
  category,
  unit,
  moverPositionId,
});

const LineItemForm = {
  UNIT: {
    MINUTE: 'MINUTE',
  },

  getDisplayPrice,
  getQuantity,
  getHasQuantityFormula,
  getCategoryForKind,
  getUnitForKind,
  getIsLineItem,
  validate,

  new: _new,
  edit,
  editFromTemplateLineItem,

  toForm,
  toMutation,
};

export default LineItemForm;
